"use client";

import type { ColumnDef } from "@tanstack/react-table";
import { Clock, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { FilterClause, SelectList, TextOperator } from "../domain";
import { FILTER_TYPE_OBJ } from "../domain";
import { buildFilterClause, clauseToOperator, formatFilterClauseLabel, operatorToClause, type SelectFilterOperator } from "../app";
import { getColumnName, resolveFilterType } from "./column-helpers";
import type { RecentFilterEntry } from "./use-recent-filters";
import { FilterInput } from "./filter-input";
import { useTableLabelFn } from "./labels-context";
import { useTableComponents, useTableConfig } from "./table-provider";

interface TableFiltersProps<TData> {
  columns: ColumnDef<TData>[];
  lists?: SelectList[] | undefined;
  filters: FilterClause[];
  onFiltersChange: (filters: FilterClause[]) => void;
  previewCount?: number;
  totalCount?: number;
  popoverColumn?: string | null;
  onChipHover?: (filter: FilterClause | null) => void;
  onClose?: () => void;
  recentFilters?: RecentFilterEntry[];
  onApplyRecent?: (filters: FilterClause[]) => void;
}

export function TableFilters<TData>({
  columns,
  lists,
  filters,
  onFiltersChange,
  previewCount,
  totalCount,
  popoverColumn,
  onChipHover,
  onClose,
  recentFilters = [],
  onApplyRecent,
}: TableFiltersProps<TData>) {
  const t = useTableLabelFn();
  const { Button, Select } = useTableComponents();
  const { locale } = useTableConfig();
  const [draftFilters, setDraftFilters] = useState<FilterClause[]>(filters);
  const [selectedColumn, setSelectedColumn] = useState<string>("");
  const [draftValue, setDraftValue] = useState<string>("");
  const [draftOperator, setDraftOperator] = useState<TextOperator>("contains");
  const [draftMin, setDraftMin] = useState<string>("");
  const [draftMax, setDraftMax] = useState<string>("");
  const [draftFrom, setDraftFrom] = useState<string>("");
  const [draftTo, setDraftTo] = useState<string>("");
  const [draftSelect, setDraftSelect] = useState<string>("");
  const [draftBool, setDraftBool] = useState<boolean>(true);
  const [draftMultiValues, setDraftMultiValues] = useState<string[]>([]);
  const [draftSelectOperator, setDraftSelectOperator] =
    useState<SelectFilterOperator>("in");
  const [draftNegate, setDraftNegate] = useState<boolean>(false);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [columnSearch, setColumnSearch] = useState<string>("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  // When popover opened from header Funnel, pre-select that column
  useEffect(() => {
    if (popoverColumn) {
      const existing = filters.find((f) => f.column === popoverColumn);
      if (existing) {
        const idx = filters.indexOf(existing);
        editFilter(idx);
      } else {
        setSelectedColumn(popoverColumn);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popoverColumn]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (draftFilters !== filters) onFiltersChange(draftFilters);
    }, 300);
    return () => clearTimeout(timer);
  }, [draftFilters, filters, onFiltersChange]);

  const filterableColumns = columns.filter((c) => {
    const meta = c.meta;
    return meta?.filterable !== "none";
  });

  const buildClause = (colName: string): FilterClause | null => {
    const col = filterableColumns.find((c) => getColumnName(c) === colName);
    if (!col) return null;
    const type = resolveFilterType(col, lists);
    if (!type) return null;
    // For list-type columns, use the operator model (no separate NOT checkbox)
    if (type === "select-single" || type === "select-multi") {
      return operatorToClause(
        colName,
        draftSelectOperator,
        draftSelect,
        draftMultiValues,
      );
    }
    // For other types, use the generic builder with draftNegate
    return buildFilterClause({
      column: colName,
      filterType: type,
      draftValue,
      draftOperator,
      draftMin,
      draftMax,
      draftFrom,
      draftTo,
      draftSelect,
      draftBool,
      draftMultiValues,
      draftNegate,
    });
  };

  const addFilter = () => {
    if (!selectedColumn) return;
    const clause = buildClause(selectedColumn);
    if (clause) {
      setDraftFilters([...draftFilters, clause]);
      resetDraft();
    }
  };

  const resetDraft = () => {
    setSelectedColumn("");
    setDraftValue("");
    setDraftOperator("contains");
    setDraftMin("");
    setDraftMax("");
    setDraftFrom("");
    setDraftTo("");
    setDraftSelect("");
    setDraftMultiValues([]);
    setDraftSelectOperator("in");
    setDraftNegate(false);
    setEditingIdx(null);
  };

  const removeFilter = (idx: number) => {
    setDraftFilters(draftFilters.filter((_, i) => i !== idx));
  };

  const reorderFilters = (from: number, to: number) => {
    if (from === to) return;
    const updated = [...draftFilters];
    const [moved] = updated.splice(from, 1);
    if (moved !== undefined) {
      updated.splice(to, 0, moved);
      setDraftFilters(updated);
    }
  };

  const editFilter = (idx: number) => {
    const f = draftFilters[idx];
    if (!f) return;
    setEditingIdx(idx);
    setSelectedColumn(f.column);
    setDraftNegate(f.negate ?? false);
    // For list-type filters, infer operator from clause and reset draftNegate
    // (operator model expresses negation through the operator, not the checkbox)
    const inferredOp = clauseToOperator(f);
    if (inferredOp) {
      setDraftSelectOperator(inferredOp);
      setDraftNegate(false);
      if (f.type === FILTER_TYPE_OBJ.SELECT_SINGLE) setDraftSelect(f.value);
      else if (f.type === FILTER_TYPE_OBJ.SELECT_MULTI)
        setDraftMultiValues(f.values);
      else {
        // empty/not-empty — clear value inputs
        setDraftSelect("");
        setDraftMultiValues([]);
      }
      return;
    }
    switch (f.type) {
      case FILTER_TYPE_OBJ.TEXT:
        setDraftValue(f.value);
        setDraftOperator(f.operator ?? "contains");
        break;
      case FILTER_TYPE_OBJ.NUMBER_RANGE:
        setDraftMin(f.min !== undefined ? String(f.min) : "");
        setDraftMax(f.max !== undefined ? String(f.max) : "");
        break;
      case FILTER_TYPE_OBJ.DATE_RANGE:
        setDraftFrom(f.from ?? "");
        setDraftTo(f.to ?? "");
        break;
      case FILTER_TYPE_OBJ.BOOLEAN:
        setDraftBool(f.value);
        break;
    }
  };

  const applyEdit = () => {
    if (editingIdx === null || !selectedColumn) return;
    const clause = buildClause(selectedColumn);
    if (clause) {
      setDraftFilters(
        draftFilters.map((f, i) => (i === editingIdx ? clause : f)),
      );
      resetDraft();
    }
  };

  const clearAll = () => setDraftFilters([]);

  const popoverRef = useRef<HTMLDivElement>(null);

  // focusTrap + Escape: keep TAB focus inside popover, close on Escape
  useEffect(() => {
    const el = popoverRef.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = el.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [onClose]);

  // outside-click: close popover when clicking outside
  useEffect(() => {
    if (!onClose) return;
    const handler = (e: MouseEvent) => {
      const el = popoverRef.current;
      if (el && e.target instanceof Node && !el.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div className="space-y-2">
      <div
        ref={popoverRef}
        role="dialog"
        aria-modal="true"
        aria-label={t("table.filters.active")}
        className="filterPopover rounded-md border p-4 space-y-3"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">
            {t("table.filters.active")}
            {previewCount !== undefined && totalCount !== undefined && (
              <span className="ml-2 text-xs text-text-secondary">
                {previewCount} / {totalCount} matches
              </span>
            )}
          </span>
          {filters.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearAll}>
              {t("table.filters.clearAll")}
            </Button>
          )}
        </div>

        {filters.length === 0 && (
          <p className="text-sm text-text-secondary">
            {t("table.filters.noActive")}
          </p>
        )}

        {filters.map((f, idx) => (
          <div
            key={idx}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOverIdx(idx);
            }}
            onDragLeave={() => setDragOverIdx(null)}
            onDrop={() => {
              if (dragIdx !== null) reorderFilters(dragIdx, idx);
              setDragIdx(null);
              setDragOverIdx(null);
            }}
            onDragEnd={() => {
              setDragIdx(null);
              setDragOverIdx(null);
            }}
            onMouseEnter={() => {
              onChipHover?.(f);
            }}
            onMouseLeave={() => {
              onChipHover?.(null);
            }}
            className={`filterChip inline-flex items-center gap-1 rounded-full bg-surface px-3 py-1 text-xs cursor-pointer hover:bg-surface/80 ${
              dragIdx === idx ? "opacity-50" : ""
            } ${dragOverIdx === idx ? "ring-2 ring-primary" : ""}`}
            onClick={() => editFilter(idx)}
          >
            <span className="font-medium">
              {formatFilterClauseLabel(f, lists, locale, {
                chipIsEmpty: t("chipIsEmpty"),
                chipIsNotEmpty: t("chipIsNotEmpty"),
              })}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                removeFilter(idx);
              }}
              className="ml-1 text-text-secondary hover:text-text-primary"
              aria-label={t("table.removeFilter")}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}

        {recentFilters.length > 0 && onApplyRecent && (
          <div className="border-t pt-2 space-y-1">
            <div className="text-xs font-medium text-text-secondary">
              {t("table.filters.recent")}
            </div>
            <div className="flex flex-wrap gap-1">
              {recentFilters.slice(0, 5).map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => onApplyRecent(entry.filters)}
                  className="inline-flex items-center gap-1 rounded-full bg-surface/60 px-2 py-0.5 text-xs hover:bg-surface"
                >
                  <Clock className="h-3 w-3" />
                  {entry.filters.map((f) => f.column).join(", ")}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="border-t pt-3 space-y-2">
          <span className="text-sm font-medium">
            {editingIdx !== null
              ? t("table.filters.edit")
              : t("table.filters.add")}
          </span>
          <input
            type="text"
            value={columnSearch}
            onChange={(e) => setColumnSearch(e.target.value)}
            placeholder={t("searchColumnsPlaceholder")}
            aria-label={t("searchColumnsAria")}
            className="w-full rounded-md border bg-background px-2 py-1 text-sm"
          />
          <div className="flex items-center gap-2">
            <Select
              size="sm"
              value={selectedColumn}
              onChange={(v) => setSelectedColumn(v)}
              ariaLabel={t("table.filters.aria.columnSelect")}
              className="w-auto min-w-[160px]"
              options={[
                { value: "", label: t("table.filters.selectColumn") },
                ...filterableColumns
                  .filter((c) => {
                    if (!columnSearch) return true;
                    const name = getColumnName(c);
                    return name
                      .toLowerCase()
                      .includes(columnSearch.toLowerCase());
                  })
                  .map((c) => {
                    const name = getColumnName(c);
                    const type = resolveFilterType(c, lists);
                    return { value: name, label: `${name} (${type})` };
                  }),
              ]}
            />
            {/* NOT checkbox — hidden for list-type columns (operator model
                expresses negation through not-equals/not-in/is-not-empty) */}
            {(() => {
              const selCol = filterableColumns.find(
                (c) => getColumnName(c) === selectedColumn,
              );
              const selType = selCol ? resolveFilterType(selCol, lists) : null;
              const isListType =
                selType === "select-single" || selType === "select-multi";
              if (!selectedColumn || isListType) return null;
              return (
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    checked={draftNegate}
                    onChange={(e) => setDraftNegate(e.target.checked)}
                  />
                  NOT
                </label>
              );
            })()}
          </div>

          {selectedColumn && (
            <FilterInput
              column={filterableColumns.find(
                (c) => getColumnName(c) === selectedColumn,
              )}
              lists={lists}
              draftValue={draftValue}
              setDraftValue={setDraftValue}
              draftOperator={draftOperator}
              setDraftOperator={setDraftOperator}
              draftMin={draftMin}
              setDraftMin={setDraftMin}
              draftMax={draftMax}
              setDraftMax={setDraftMax}
              draftFrom={draftFrom}
              setDraftFrom={setDraftFrom}
              draftTo={draftTo}
              setDraftTo={setDraftTo}
              draftSelect={draftSelect}
              setDraftSelect={setDraftSelect}
              draftBool={draftBool}
              setDraftBool={setDraftBool}
              draftMultiValues={draftMultiValues}
              setDraftMultiValues={setDraftMultiValues}
              draftSelectOperator={draftSelectOperator}
              setDraftSelectOperator={setDraftSelectOperator}
              onAdd={editingIdx !== null ? applyEdit : addFilter}
            />
          )}

          {selectedColumn && (
            <Button
              variant="outline"
              size="sm"
              onClick={editingIdx !== null ? applyEdit : addFilter}
            >
              {editingIdx !== null
                ? t("table.filters.apply")
                : t("table.filters.addBtn")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
